import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'

const emptyForm = {
  questionEn: '',
  answerEn: '',
  questionAm: '',
  answerAm: '',
  keywords: '',
  category: 'Assistant',
  isPublished: '1',
  sortOrder: '0',
}

export default function AssistantQAAdmin() {
  const [rows, setRows] = useState([])
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)

  const load = async () => {
    const { data } = await api.get('/faqs/admin')
    setRows(data.data || [])
  }

  useEffect(() => { load().catch(() => setRows([])) }, [])

  const pairs = useMemo(() => {
    const map = new Map()
    rows.forEach((row) => {
      const key = row.pairId || `single-${row.id}`
      if (!map.has(key)) {
        map.set(key, { pairId: row.pairId, category: row.category, isPublished: row.isPublished, sortOrder: row.sortOrder, en: null, am: null })
      }
      const item = map.get(key)
      if (row.language === 'am') item.am = row
      else item.en = row
    })
    return Array.from(map.values())
  }, [rows])

  const startCreate = () => {
    setForm(emptyForm)
    setEditing('new')
  }

  const startEdit = (pair) => {
    setForm({
      questionEn: pair.en?.question || '',
      answerEn: pair.en?.answer || '',
      questionAm: pair.am?.question || '',
      answerAm: pair.am?.answer || '',
      keywords: pair.en?.keywords || pair.am?.keywords || '',
      category: pair.category || 'Assistant',
      isPublished: pair.isPublished ? '1' : '0',
      sortOrder: String(pair.sortOrder || 0),
      pairId: pair.pairId || '',
      enId: pair.en?.id,
      amId: pair.am?.id,
    })
    setEditing(pair)
  }

  const save = async (e) => {
    e.preventDefault()
    await api.post('/faqs/bilingual', form)
    setEditing(null)
    await load()
  }

  const remove = async (pair) => {
    if (!confirm('Delete this Q&A in both languages?')) return
    await api.delete(`/faqs/pair/${pair.pairId || pair.en?.id || pair.am?.id}`)
    await load()
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">AI Assistant Q&A</h1>
          <p className="text-sm text-slate-400">Add questions and answers in English and Amharic. These power the Fortino AI Assistant.</p>
        </div>
        <button onClick={startCreate} className="admin-btn">Add Q&A</button>
      </div>

      {editing && (
        <form onSubmit={save} className="admin-glass rounded-2xl p-5 mb-6 grid md:grid-cols-2 gap-4">
          <label className="md:col-span-2">
            <span className="text-sm text-slate-400">Question (English)</span>
            <input className="admin-input mt-1" value={form.questionEn} onChange={(e) => setForm({ ...form, questionEn: e.target.value })} />
          </label>
          <label className="md:col-span-2">
            <span className="text-sm text-slate-400">Answer (English)</span>
            <textarea rows={4} className="admin-input mt-1" value={form.answerEn} onChange={(e) => setForm({ ...form, answerEn: e.target.value })} />
          </label>
          <label className="md:col-span-2">
            <span className="text-sm text-slate-400">ጥያቄ (አማርኛ)</span>
            <input className="admin-input mt-1" value={form.questionAm} onChange={(e) => setForm({ ...form, questionAm: e.target.value })} />
          </label>
          <label className="md:col-span-2">
            <span className="text-sm text-slate-400">መልስ (አማርኛ)</span>
            <textarea rows={4} className="admin-input mt-1" value={form.answerAm} onChange={(e) => setForm({ ...form, answerAm: e.target.value })} />
          </label>
          <label>
            <span className="text-sm text-slate-400">Keywords</span>
            <input className="admin-input mt-1" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} />
          </label>
          <label>
            <span className="text-sm text-slate-400">Category</span>
            <input className="admin-input mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </label>
          <label>
            <span className="text-sm text-slate-400">Status</span>
            <select className="w-full mt-1 px-3 py-2 rounded-xl bg-slate-800 border border-slate-700" value={form.isPublished} onChange={(e) => setForm({ ...form, isPublished: e.target.value })}>
              <option value="1">Published</option>
              <option value="0">Hidden</option>
            </select>
          </label>
          <div className="md:col-span-2 flex gap-3">
            <button className="px-4 py-2 rounded-xl bg-blue-600">Save both languages</button>
            <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded-xl bg-slate-800">Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {pairs.map((pair) => (
          <div key={pair.pairId || pair.en?.id || pair.am?.id} className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
            <p className="font-semibold">{pair.en?.question || pair.am?.question}</p>
            <p className="text-sm text-slate-400 mt-1">{pair.am?.question}</p>
            <p className="text-xs text-slate-500 mt-2">{pair.category} · {pair.isPublished ? 'Published' : 'Hidden'}</p>
            <div className="mt-3 flex gap-3">
              <button onClick={() => startEdit(pair)} className="text-blue-400">Edit</button>
              <button onClick={() => remove(pair)} className="text-red-400">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
